import { useState } from 'react'
import MarkdownContent from './MarkdownContent'
import { PermissionGroupItem, PermissionGroupRequestPart } from './aiAssistantModel'
import { historyCaptionStyle } from './historyStyles'
import {
  groupCountLabel,
  groupOutcomeLabel,
  isGroupSettled,
  outcomeTone,
  partitionRows,
  rowStatusLabel,
  showRetry,
} from './writeConfirmGroupModel'

type Props = {
  part: PermissionGroupRequestPart
  interactive: boolean
  onDecide: (groupId: string, approved: boolean) => void
}

const buttonStyle = (interactive: boolean, primary: boolean) => ({
  padding: '6px 16px',
  borderRadius: 8,
  border: primary ? '1px solid #1677ff' : '1px solid #d9d9d9',
  background: primary ? '#1677ff' : '#fff',
  color: primary ? '#fff' : '#333',
  cursor: interactive ? 'pointer' : 'default',
  opacity: interactive ? 1 : 0.6,
  font: 'inherit',
})

const expanderStyle = {
  padding: 0,
  border: 'none',
  background: 'none',
  color: '#1677ff',
  cursor: 'pointer',
  font: 'inherit',
  textDecoration: 'underline',
}

const badgeStyle = (tone: 'good' | 'bad' | 'muted') => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 12,
  border: `1px solid ${tone === 'bad' ? '#ffa39e' : tone === 'good' ? '#91caff' : '#d9d9d9'}`,
  background: tone === 'bad' ? '#fff1f0' : tone === 'good' ? '#e6f4ff' : '#fafafa',
  color: tone === 'bad' ? '#a8071a' : tone === 'good' ? '#0958d9' : '#666',
  fontSize: 12,
  whiteSpace: 'nowrap' as const,
})

const rowBodyStyle = {
  margin: '6px 0 0',
  padding: '6px 8px',
  border: '1px solid #f0e0a0',
  borderRadius: 6,
  background: '#fff',
  maxHeight: 200,
  overflowY: 'auto' as const,
}

// Presentational only: one row's own state. The card-level outcome tone lives
// in the model, beside the label it has to agree with.
const statusTone = (status: PermissionGroupItem['status']): 'good' | 'bad' | 'muted' =>
  status === 'applied' ? 'good' : status === 'failed' ? 'bad' : 'muted'

const Row = ({ item }: { item: PermissionGroupItem }) => {
  const label = rowStatusLabel(item.status)

  return (
    <li style={{ margin: '0 0 8px' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ flex: 1 }}>{item.summary}</span>
        {label && <span style={badgeStyle(statusTone(item.status))}>{label}</span>}
      </div>
      {item.body && (
        <div style={rowBodyStyle}>
          <MarkdownContent content={item.body} />
        </div>
      )}
    </li>
  )
}

/**
 * One approval covering N same-type writes (chat#169).
 *
 * Summary and count by default, rows on request. There are deliberately no
 * per-row approve controls: the group decision governs, so a user cannot
 * half-approve an intent they made once.
 *
 * A partial failure turns Approve/Reject into Retry/Cancel, which post the same
 * group decision — the server re-runs only the rows still pending, so a row that
 * already succeeded can never be applied twice.
 */
const WriteConfirmGroup = ({ part, interactive, onDecide }: Props) => {
  const [expanded, setExpanded] = useState(false)
  const rows = part.items
  const { outstanding } = partitionRows(rows)
  const failed = rows.filter((r) => r.status === 'failed')
  const decided = isGroupSettled(rows, part.status)
  const recovering = failed.length > 0 && !decided
  const canRetry = showRetry(rows)

  return (
    <div
      style={{
        margin: '8px 0',
        border: '1px solid #ffe58f',
        background: '#fffbe6',
        borderRadius: 8,
        padding: '12px',
      }}
    >
      <p style={{ margin: '0 0 4px', fontWeight: 500 }}>
        {recovering ? 'Some changes did not apply' : 'Approve these changes?'}
      </p>
      <p style={{ margin: '0 0 6px' }}>{part.summary}</p>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={expanderStyle}
        aria-expanded={expanded}
      >
        {expanded ? `Hide ${groupCountLabel(rows.length)}` : `Show ${groupCountLabel(rows.length)}`}
      </button>

      {expanded && (
        <ul style={{ margin: '8px 0 12px', padding: '0 0 0 4px', listStyle: 'none' }}>
          {rows.map((item) => (
            <Row key={item.tool_call_id} item={item} />
          ))}
        </ul>
      )}

      {recovering && (
        <ul style={{ margin: '8px 0 12px', padding: '0 0 0 18px' }}>
          {failed.map((item) => (
            <li key={item.tool_call_id} style={{ color: '#a8071a' }}>
              {item.summary} {item.error && <span>— {item.error}</span>}
            </li>
          ))}
        </ul>
      )}

      {decided ? (
        <span style={badgeStyle(outcomeTone(rows))}>{groupOutcomeLabel(rows)}</span>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: expanded ? 0 : 10 }}>
            {recovering ? (
              <>
                {canRetry && (
                  <button
                    type="button"
                    disabled={!interactive}
                    onClick={() => onDecide(part.group_id, true)}
                    style={buttonStyle(interactive, true)}
                  >
                    Retry
                  </button>
                )}
                <button
                  type="button"
                  disabled={!interactive}
                  onClick={() => onDecide(part.group_id, false)}
                  style={buttonStyle(interactive, false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={!interactive}
                  onClick={() => onDecide(part.group_id, true)}
                  style={buttonStyle(interactive, true)}
                >
                  Approve
                </button>
                <button
                  type="button"
                  disabled={!interactive}
                  onClick={() => onDecide(part.group_id, false)}
                  style={buttonStyle(interactive, false)}
                >
                  Reject
                </button>
              </>
            )}
          </div>
          {/* Historical and still undecided: the user walked away from it. */}
          {part.status === 'pending' && outstanding.length > 0 && (
            <p style={historyCaptionStyle}>No decision was made.</p>
          )}
        </>
      )}
    </div>
  )
}

export default WriteConfirmGroup
